import type { LocalizedString, LocalizedStringArray } from '../types';
import { Locale } from '../types';
/**
 * Helper function to get localized string
 * Falls back to English if the specified locale is not available
 */
export const getLocalizedString = (
  localizedString: LocalizedString | undefined,
  locale: Locale
): string => {
  if (!localizedString) return '';
  return localizedString[locale] || localizedString[Locale.en] || '';
};

/**
 * Helper function to get localized string array
 * Falls back to English if the specified locale is not available
 */
export const getLocalizedStringArray = (
  localizedArray: LocalizedStringArray | undefined,
  locale: Locale
): string[] => {
  if (!localizedArray) return [];
  return localizedArray[locale] || localizedArray[Locale.en] || [];
};

/**
 * Helper function to format days with localization
 */
export const formatDaysLocalized = (days: number, locale: Locale): string => {
  const weeks = +(days / 7).toFixed(1);

  // Format days to show decimals only if they exist
  const formattedDays = days % 1 === 0 ? days.toString() : days.toFixed(1);
  const daysInt = Math.floor(days);

  const dayWord = locale === Locale.ru ?
    (daysInt === 1 ? 'день' : daysInt < 5 ? 'дня' : 'дней') :
    locale === Locale.kz ? 'күн' :
      (daysInt === 1 ? 'day' : 'days');

  const weeksInt = Math.floor(weeks);
  const weekWord = locale === Locale.ru ?
    (weeksInt === 1 ? 'неделя' : weeksInt < 5 ? 'недели' : 'недель') :
    locale === Locale.kz ? 'апта' :
      (weeksInt === 1 ? 'week' : 'weeks');

  const formattedWeeks = weeks % 1 === 0 ? weeks.toString() : weeks.toFixed(1);
  return `${formattedDays} ${dayWord} (${formattedWeeks} ${weekWord})`;
};

/**
 * Helper function to format deadline with localization
 */
export const formatDeadlineLocalized = (days: number, locale: Locale): string => {
  if (days === 0) {
    const noSelection = {
      [Locale.en]: 'No selection',
      [Locale.ru]: 'Нет выбора',
      [Locale.kz]: 'Таңдау жоқ'
    };
    return noSelection[locale];
  }

  // Format days to show decimals only if they exist
  const formattedDays = days % 1 === 0 ? days.toString() : days.toFixed(1);
  const daysInt = Math.floor(days);

  const weeks = +(days / 7).toFixed(1);
  const months = +(days / 30).toFixed(1);

  const dayWord = locale === Locale.ru ?
    (daysInt === 1 ? 'день' : daysInt < 5 ? 'дня' : 'дней') :
    locale === Locale.kz ? 'күн' :
      (daysInt === 1 ? 'day' : 'days');

  if (months >= 2) {
    const monthsInt = Math.floor(months);
    const monthWord = locale === Locale.ru ?
      (monthsInt === 1 ? 'месяц' : monthsInt < 5 ? 'месяца' : 'месяцев') :
      locale === Locale.kz ? 'ай' :
        (monthsInt === 1 ? 'month' : 'months');
    const formattedMonths = months % 1 === 0 ? months.toString() : months.toFixed(1);
    return `${formattedDays} ${dayWord} (≈${formattedMonths} ${monthWord})`;
  }

  const weeksInt = Math.floor(weeks);
  const weekWord = locale === Locale.ru ?
    (weeksInt === 1 ? 'неделя' : weeksInt < 5 ? 'недели' : 'недель') :
    locale === Locale.kz ? 'апта' :
      (weeksInt === 1 ? 'week' : 'weeks');

  const formattedWeeks = weeks % 1 === 0 ? weeks.toString() : weeks.toFixed(1);
  return `${formattedDays} ${dayWord} (≈${formattedWeeks} ${weekWord})`;
};
